import { useQuery } from '@tanstack/react-query';
import { addMinutes } from 'date-fns';
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';

import {
    Button,
    DrawerFooter,
    Dropdown,
    Field,
    Input,
    MonoLabel,
    Textarea,
    Toggle,
    cn,
    focusRing,
} from '@/design-system';
import {
    CLIENT_TYPE_LABELS,
    CLIENT_TYPES,
    clientApi,
    formatClientAddress,
    sameClientAddress,
    type Client,
    type ClientAddress,
    type ClientType,
} from '@/entities/client';
import {
    describeOrderProblem,
    type InspectionType,
    type Order,
    type OrderProblem,
} from '@/entities/order';
import { ulid } from '@/shared/api';
import { InspectorPicker, useAddressLookup } from '@/shared/ui';

import { inspectionsCopy as copy } from '../copy';
import { useCreateOrder, useDebounced } from '../hooks';
import { DURATIONS, localMoment, toApiMoment } from '../moments';
import { useObjectAddress } from '../objectAddress';
import { serviceChoices } from '../serviceChoices';
import { ClientHomeFields } from './ClientHomeFields';
import { ObjectAddressFields } from './ObjectAddressFields';
import { ProblemLine } from './ProblemLine';

export interface NewOrderFormProps {
    inspectionTypes: readonly InspectionType[];
    onCreated: (order: Order) => void;
    onCancel: () => void;
}

type ClientMode = 'existing' | 'new';

/**
 * "Nieuwe keuring": opdrachtgever, object, dienst en - als het kan - meteen de
 * afspraak. Het object komt uit de BAG zodra postcode en huisnummer er staan
 * ({@link useObjectAddress}); kent de BAG het niet, dan vul je straat en plaats
 * zelf in. Het huidige adres van de klant zoekt op dezelfde manier
 * ({@link ClientHomeFields}) en komt bij de klant te staan: bij een nieuwe klant
 * meteen, bij een bestaande klant alleen als het veranderde.
 *
 * Een opdrachtgever of object dat onderweg al is aangemaakt wordt bij een
 * tweede poging hergebruikt, en de keuring zelf gaat met een vaste
 * idempotency key: twee keer op de knop drukken levert één keuring op.
 * Te weinig tegoed (402) wordt een zin met een weg naar het tegoed.
 */
export function NewOrderForm({ inspectionTypes, onCreated, onCancel }: NewOrderFormProps) {
    const [idempotencyKey] = useState(() => ulid());

    /* Opdrachtgever */
    const [clientMode, setClientMode] = useState<ClientMode>('existing');
    const [term, setTerm] = useState('');
    const [client, setClient] = useState<Client | null>(null);
    const [clientType, setClientType] = useState<ClientType>('private');
    const [clientName, setClientName] = useState('');
    const [contactName, setContactName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');

    /* Huidig adres van de klant: een nieuwe klant typt het, een gekozen klant heeft het misschien al. */
    const newClientHome = useAddressLookup();
    const knownClientHome = useAddressLookup();
    const [changingHome, setChangingHome] = useState(false);
    const [homeIsObject, setHomeIsObject] = useState(false);

    /* Object */
    const address = useObjectAddress();

    /* Dienst en afspraak */
    const [serviceKey, setServiceKey] = useState('');
    const [separateServices, setSeparateServices] = useState(false);
    const [scheduleNow, setScheduleNow] = useState(false);
    const [assigneeUuid, setAssigneeUuid] = useState('');
    const [date, setDate] = useState('');
    const [time, setTime] = useState('09:00');
    const [duration, setDuration] = useState(120);
    const [notes, setNotes] = useState('');

    const [problem, setProblem] = useState<OrderProblem | null>(null);
    const [missing, setMissing] = useState<string | null>(null);

    /*
     * De keuzes bij "Dienst". De losse keuring en losse NHG-bijlage staan er pas bij
     * als je om een andere samenstelling vraagt; is er maar één keuze, dan is die de
     * keuze. De diensten komen binnen terwijl het paneel al open kan staan, dus de
     * keuze wordt afgeleid en niet één keer bij het openen bepaald.
     */
    const choices = useMemo(() => serviceChoices(inspectionTypes), [inspectionTypes]);
    const offered = separateServices ? choices : choices.filter((choice) => !choice.separate);
    const canSeparate = !separateServices && choices.some((choice) => choice.separate);
    const service =
        choices.find((choice) => choice.key === serviceKey) ??
        (offered.length === 1 ? offered[0] : undefined);

    const searchTerm = useDebounced(term, 250);
    const matches = useQuery({
        queryKey: ['clients', 'search', searchTerm],
        queryFn: () => clientApi.search(searchTerm),
        enabled: clientMode === 'existing' && client === null && searchTerm.trim().length >= 2,
    });
    const create = useCreateOrder();
    const [preparing, setPreparing] = useState(false);
    const busy = preparing || create.isPending;

    const home = client ? knownClientHome : newClientHome;
    const savedHome = client ? formatClientAddress(client.address) : null;
    const keepsSavedHome = savedHome !== null && !changingHome && !homeIsObject;

    /**
     * Het adres dat de klant krijgt: `undefined` als het bewaarde adres blijft, `null`
     * zolang het getypte adres niet af is.
     */
    function homeToSave(): ClientAddress | null | undefined {
        if (keepsSavedHome) {
            return undefined;
        }

        return homeIsObject ? address.resolved : home.resolved;
    }

    /** Een gekozen klant: het adresblok begint bij het adres dat bij die klant staat. */
    function pickClient(picked: Client): void {
        setClient(picked);
        knownClientHome.load(picked.address);
        setChangingHome(false);
        setHomeIsObject(false);
    }

    /**
     * De klant zoals hij nu bewaard is. Het adresblok blijft staan zoals het stond:
     * volgt het adres het object en mislukt de keuring, dan gaat een verbeterd
     * object bij de tweede poging ook naar de klant.
     */
    function keepClient(saved: Client): Client {
        setClient(saved);
        knownClientHome.load(saved.address);

        return saved;
    }

    async function ensureClient(): Promise<Client> {
        const wanted = homeToSave();

        if (client) {
            return !wanted || sameClientAddress(client.address, wanted)
                ? client
                : keepClient(await clientApi.updateAddress(client.uuid, wanted));
        }

        return keepClient(
            await clientApi.create({
                type: clientType,
                name: clientName.trim(),
                contactName,
                email,
                phone,
                address: wanted ?? undefined,
            }),
        );
    }

    function validationMessage(): string | null {
        if (clientMode === 'existing' ? client === null : clientName.trim() === '') {
            return copy.newMissingClient;
        }

        if (!homeIsObject && homeToSave() === null) {
            return copy.newMissingClientHome;
        }

        if (!address.ready) {
            return copy.newMissingProperty;
        }

        if (service === undefined) {
            return copy.newMissingService;
        }

        if (scheduleNow && (assigneeUuid === '' || date === '' || time === '')) {
            return copy.scheduleMissing;
        }

        return null;
    }

    async function submit(event: FormEvent) {
        event.preventDefault();
        setProblem(null);

        const message = validationMessage();

        setMissing(message);

        if (message || service === undefined) {
            return;
        }

        setPreparing(true);

        try {
            const [owner, propertyUuid] = await Promise.all([
                ensureClient(),
                address.ensureProperty(),
            ]);
            const moment = scheduleNow ? localMoment(date, time) : null;

            setPreparing(false);
            create.mutate(
                {
                    idempotencyKey,
                    order: {
                        clientUuid: owner.uuid,
                        propertyUuid,
                        inspectionTypeUuids: service.typeUuids,
                        assigneeUuid: scheduleNow ? assigneeUuid : null,
                        scheduledAt: moment ? toApiMoment(moment) : null,
                        scheduledEndAt: moment ? toApiMoment(addMinutes(moment, duration)) : null,
                        notes: notes.trim() || null,
                    },
                },
                {
                    onSuccess: onCreated,
                    onError: (error) => setProblem(describeOrderProblem(error)),
                },
            );
        } catch (error) {
            setPreparing(false);
            setProblem(describeOrderProblem(error));
        }
    }

    return (
        <form
            onSubmit={(event) => void submit(event)}
            className="flex flex-1 flex-col pt-5"
            noValidate
        >
            <Section number="01" title={copy.newClientSection}>
                {client ? (
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-[8px] border border-[var(--accent-border)] bg-[var(--accent-tint-3)] px-3 py-[10px]">
                        <div className="min-w-0">
                            <div className="truncate text-[14px] font-medium text-[var(--text-primary)]">
                                {client.name}
                            </div>
                            <div className="text-[12px] text-[var(--text-tertiary-safe)]">
                                {[client.typeLabel, client.contactName, client.email]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </div>
                        </div>
                        <Button
                            variant="ghost"
                            onClick={() => {
                                setClient(null);
                                setClientMode('existing');
                            }}
                        >
                            {copy.newClientChange}
                        </Button>
                    </div>
                ) : (
                    <>
                        <div
                            className="flex flex-wrap gap-2"
                            role="group"
                            aria-label={copy.newClientSection}
                        >
                            {(['existing', 'new'] as const).map((option) => (
                                <Button
                                    key={option}
                                    variant={clientMode === option ? 'accentSoft' : 'outline'}
                                    size="sm"
                                    aria-pressed={clientMode === option}
                                    onClick={() => setClientMode(option)}
                                >
                                    {option === 'existing'
                                        ? copy.newClientExisting
                                        : copy.newClientNew}
                                </Button>
                            ))}
                        </div>

                        {clientMode === 'existing' ? (
                            <>
                                <Field label={copy.newClientSearch} size="sm">
                                    <Input
                                        type="search"
                                        value={term}
                                        placeholder={copy.newClientSearchPlaceholder}
                                        onChange={(event) => setTerm(event.target.value)}
                                    />
                                </Field>
                                {matches.isFetching ? (
                                    <MonoLabel size={11} tone="muted">
                                        {copy.newClientSearching}
                                    </MonoLabel>
                                ) : null}
                                {matches.data && matches.data.length === 0 ? (
                                    <p className="m-0 text-[13px] text-[var(--text-tertiary-safe)]">
                                        {copy.newClientNone}
                                    </p>
                                ) : null}
                                {matches.data && matches.data.length > 0 ? (
                                    <ul
                                        aria-label={copy.newClientResults}
                                        className="m-0 grid list-none gap-[6px] p-0"
                                    >
                                        {matches.data.map((match) => (
                                            <li key={match.uuid}>
                                                <button
                                                    type="button"
                                                    onClick={() => pickClient(match)}
                                                    className={cn(
                                                        'flex min-h-[44px] w-full cursor-pointer flex-col items-start rounded-[8px] border border-[var(--border-control)] bg-[var(--surface-card)] px-3 py-2 text-left text-[var(--text-primary)] hover:border-[var(--accent)]',
                                                        focusRing,
                                                    )}
                                                >
                                                    <span className="text-[13px] font-medium">
                                                        {match.name}
                                                    </span>
                                                    <span className="text-[12px] text-[var(--text-tertiary-safe)]">
                                                        {[
                                                            match.typeLabel,
                                                            match.contactName,
                                                            match.email,
                                                        ]
                                                            .filter(Boolean)
                                                            .join(' · ')}
                                                    </span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                ) : null}
                            </>
                        ) : (
                            <div className="grid gap-3">
                                <Field label={copy.newClientType} size="sm">
                                    <Dropdown
                                        value={clientType}
                                        options={CLIENT_TYPES.map((type) => ({
                                            value: type,
                                            label: CLIENT_TYPE_LABELS[type],
                                        }))}
                                        onChange={setClientType}
                                    />
                                </Field>
                                <Field label={copy.newClientName} size="sm">
                                    <Input
                                        value={clientName}
                                        onChange={(event) => setClientName(event.target.value)}
                                    />
                                </Field>
                                <Field label={copy.newClientContact} size="sm">
                                    <Input
                                        value={contactName}
                                        onChange={(event) => setContactName(event.target.value)}
                                    />
                                </Field>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label={copy.newClientEmail} size="sm">
                                        <Input
                                            type="email"
                                            value={email}
                                            onChange={(event) => setEmail(event.target.value)}
                                        />
                                    </Field>
                                    <Field label={copy.newClientPhone} size="sm">
                                        <Input
                                            type="tel"
                                            value={phone}
                                            onChange={(event) => setPhone(event.target.value)}
                                        />
                                    </Field>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {client || clientMode === 'new' ? (
                    <ClientHomeFields
                        clientType={client ? client.type : clientType}
                        home={home}
                        savedAddress={savedHome}
                        changing={changingHome}
                        onChange={() => setChangingHome(true)}
                        sameAsObject={homeIsObject}
                        onSameAsObjectChange={setHomeIsObject}
                        objectAddress={
                            address.resolved ? formatClientAddress(address.resolved) : null
                        }
                    />
                ) : null}
            </Section>

            <Section number="02" title={copy.newPropertySection}>
                <ObjectAddressFields address={address} />
            </Section>

            <Section number="03" title={copy.newServiceSection}>
                <Field
                    label={copy.newService}
                    size="sm"
                    description={separateServices ? copy.newServiceSeparateHint : undefined}
                >
                    <Dropdown
                        value={service?.key ?? ''}
                        placeholder={copy.newServicePick}
                        options={offered.map((choice) => ({
                            value: choice.key,
                            label: choice.label,
                            hint: choice.hint,
                        }))}
                        onChange={setServiceKey}
                    />
                </Field>

                {/* De keuring en de NHG-bijlage horen bij elkaar; los kiezen kan, maar
                    dan vraag je erom. */}
                {canSeparate ? (
                    <div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setServiceKey(service?.key ?? '');
                                setSeparateServices(true);
                            }}
                        >
                            {copy.newServiceSeparate}
                        </Button>
                    </div>
                ) : null}

                <Toggle
                    checked={scheduleNow}
                    onCheckedChange={setScheduleNow}
                    label={copy.newScheduleNow}
                    description={copy.newScheduleNowHint}
                />

                {scheduleNow ? (
                    <div className="grid gap-3 rounded-[9px] border border-[var(--border-subtle)] bg-[var(--surface-subtle)] p-3">
                        <Field label={copy.scheduleInspector} size="sm">
                            <InspectorPicker value={assigneeUuid} onChange={setAssigneeUuid} />
                        </Field>
                        <div className="grid grid-cols-3 gap-3">
                            <Field label={copy.scheduleDate} size="sm">
                                <Input
                                    type="date"
                                    value={date}
                                    onChange={(event) => setDate(event.target.value)}
                                />
                            </Field>
                            <Field label={copy.scheduleTime} size="sm">
                                <Input
                                    type="time"
                                    step={900}
                                    value={time}
                                    onChange={(event) => setTime(event.target.value)}
                                />
                            </Field>
                            <Field label={copy.scheduleDuration} size="sm">
                                <Dropdown
                                    value={String(duration)}
                                    options={DURATIONS.map((minutes) => ({
                                        value: String(minutes),
                                        label: copy.durationOption(minutes),
                                    }))}
                                    onChange={(minutes) => setDuration(Number(minutes))}
                                />
                            </Field>
                        </div>
                    </div>
                ) : null}

                <Field label={copy.newNotes} size="sm">
                    <Textarea
                        rows={3}
                        value={notes}
                        placeholder={copy.newNotesPlaceholder}
                        onChange={(event) => setNotes(event.target.value)}
                    />
                </Field>
            </Section>

            {missing ? (
                <p
                    role="alert"
                    className="m-0 mx-5 mb-4 text-[13px] leading-[1.4] text-[var(--danger)]"
                >
                    {missing}
                </p>
            ) : null}

            {problem ? <ProblemLine problem={problem} className="mx-5 mb-4" /> : null}

            {/* Vast onderaan het paneel, ook als het formulier lang wordt. */}
            <DrawerFooter>
                <Button type="submit" variant="primary" size="md" icon="add" disabled={busy}>
                    {busy ? copy.newSubmitting : copy.newSubmit}
                </Button>
                <Button variant="outline" size="md" onClick={onCancel}>
                    {copy.newCancel}
                </Button>
            </DrawerFooter>
        </form>
    );
}

/**
 * Eén genummerd deel van het formulier. Tussen de delen staat een lijn, zodat
 * klant, object en dienst in het paneel als drie stappen lezen.
 */
function Section({
    number,
    title,
    children,
}: {
    number: string;
    title: string;
    children: ReactNode;
}) {
    return (
        <fieldset className="m-0 mx-5 mb-6 grid min-w-0 gap-3 border-0 border-t border-[var(--border-subtle)] p-0 pt-6 first:border-t-0 first:pt-0">
            <legend className="float-left mb-1 flex w-full items-baseline gap-2 p-0">
                <MonoLabel size={10} tone="accent" aria-hidden="true">
                    {number}
                </MonoLabel>
                <span className="font-[family-name:var(--font-display)] text-[16px] leading-[1.3] font-semibold text-[var(--text-primary)]">
                    {title}
                </span>
            </legend>
            {children}
        </fieldset>
    );
}
